<template>
  <div class="media-score-page">
    <div class="card filter-card">
      <div class="date-filter">
        <el-row :gutter="15" type="flex" justify="space-between">
          <el-col :span="14" class="search">
            <label class="label">媒体名称</label>
            <el-select
              class="search-input"
              size="small"
              placeholder="请选择媒体名称"
              v-model="selectedMedia"
              clearable
              filterable
              multiple
              collapse-tags
              @change="onMediaChange"
              @clear="clearSearch"
            >
              <el-option
                v-for="media in mediaList"
                :key="media.name"
                :label="media.name"
                :value="media.name"
              />
            </el-select>
            <div class="search-tag" v-if="searchTag">
              <span class="text">搜索： {{ searchTag }}</span>
              <i class="close el-tag__close el-icon-close" @click="clearSearch"></i>
            </div>
          </el-col>
          <el-col :span="6" style="text-align: right;flex-shrink: 0;">
            <el-button
              size="small"
              @click="searchData"
              type="primary"
            >搜索</el-button>
            <el-button
              size="small"
              @click="resetForm"
            >重置</el-button>
          </el-col>
        </el-row>
      </div>
    </div>
    <div class="card content-card">
      <div class="header">
        <div class="header-left">
          <el-button type="primary" size="small" @click="onAddMedia">新增媒体</el-button>
          <el-button type="primary" size="small" @click="onCalculate" style="margin-left: 10px;">计算</el-button>
          <el-button type="success" size="small" @click="onSave" style="margin-left: 10px;">保存</el-button>
        </div>
        <div class="header-right">
          <el-button type="success" size="small" @click="onDownload">下载</el-button>
        </div>
      </div>
      <div class="content">
        <el-table
          :data="pagedTableData"
          v-loading="tableLoading"
          size="medium"
          style="width: 100%"
          :row-class-name="getRowClassName"
        >
          <el-table-column
            v-for="(col, idx) in columns"
            :key="col.prop"
            :label="col.label"
            align="left"
            header-align="left"
            :width="col.width"
            :min-width="col.minWidth"
          >
            <template v-slot="scope">
              <!-- 权重配置行 -->
              <div v-if="scope.row.isWeightRow" class="weight-cell">
                <div v-if="col.prop === 'name'" class="weight-label">
                  权重配置
                </div>
                <div v-else-if="col.prop !== 'action'" class="weight-input-container">
                  <el-input
                    v-model.number="weights[col.prop]"
                    size="mini"
                    class="weight-input"
                    @change="onWeightChange(col.prop)"
                    :min="0"
                    :max="100"
                    type="number"
                  >
                    <template slot="append">%</template>
                  </el-input>
                </div>
                <div v-else>
                  <!-- 操作列为空 -->
                </div>
              </div>
              <!-- 普通数据行 -->
              <div v-else>
                <span v-if="col.prop === 'name'">
                  <span class="media-name-cell">
                    <img :src="getMediaLogo(scope.row.name)" class="media-logo" alt="logo" />
                    {{ scope.row[col.prop] }}
                  </span>
                </span>
                <span v-else-if="col.prop !== 'action'">{{ scope.row[col.prop] }}</span>
                <button v-else class="plain-detail-btn" @click="onViewDetail(scope.row)">查看明细</button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <!-- 删除/注释局部分页样式 -->
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next, jumper"
          :total="filteredMediaList.length"
          :page-size="pageSize"
          :current-page="currentPage"
          :page-sizes="[10, 20, 40, 80]"
          @current-change="handlePageChange"
          @size-change="handleSizeChange"
        />
      </div>
    </div>
    
    <!-- 明细弹窗 -->
    <el-dialog
      :title="detailDialogTitle"
      :visible.sync="detailDialogVisible"
      width="80%"
      :before-close="handleClose"
      custom-class="detail-dialog"
    >
      <div class="detail-content">
        <el-table
          :data="detailData"
          style="width: 100%"
          :show-header="true"
          border
          stripe
        >
          <el-table-column
            prop="name"
            label="维度"
            width="200"
            align="left"
            header-align="left"
          >
            <template slot-scope="scope">
              <div class="dimension-title">{{ scope.row.name }}</div>
            </template>
          </el-table-column>
          <el-table-column
            prop="groups"
            label="项目"
            align="left"
            header-align="left"
          >
            <template slot-scope="scope">
              <div class="dimension-groups">
                <div v-for="(group, groupIndex) in scope.row.groups" :key="groupIndex" class="tag-group">
                  <div v-if="scope.row.groups.length > 1" class="group-title">{{ group.name }}</div>
                  <div class="tag-list">
                    <span
                      v-for="(tag, tagIndex) in group.tags"
                      :key="tagIndex"
                      class="custom-tag"
                      :style="getTagColorStyleByGroup(group.name)"
                    >
                      {{ tag.name }}
                    </span>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            prop="score"
            label="得分"
            width="200"
            align="center"
            header-align="center"
          >
            <template slot-scope="scope">
              <div class="dimension-score">{{ scope.row.score }}</div>
            </template>
          </el-table-column>
        </el-table>
      </div>
      <div slot="footer" class="dialog-footer">
        <el-button @click="detailDialogVisible = false" type="primary">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { 
  getOriginalDetailData,
  getOriginalMediaDetail,
  getMediaScoreList,
  getWeightConfig, 
  updateWeightConfig,
  validateWeights,
  calculateMediaScores,
  saveMediaScoreList
} from '@/utils/mediaScoreData'

// 自动批量导入assets下所有图片，建立映射
const logoContext = require.context('@/assets', false, /\.(png|jpe?g|webp)$/);
const logoMap = {};
logoContext.keys().forEach(key => {
  // key 形如 './weibo.png'
  const name = key.replace(/^\.\//, '').replace(/\.(png|jpe?g|webp)$/, '');
  logoMap[name] = logoContext(key);
});
const mediaPinyinMap = {
  '微博': 'weibo',
  '抖音': 'douyin',
  '小红书': 'xiaohongshu',
  '快手': 'kuaishou',
  'B站': 'bilibili',
  '知乎': 'zhihu',
  '携程': 'xiecheng',
  '去哪儿': 'qunaer',
  '滴滴出行': 'didichuxing',
  '爱奇艺': 'aiqiyi',
  '优酷': 'youku',
  '腾讯视频': 'tengxunshipin',
  '芒果': 'mangguo',
  '淘宝': 'taobao',
  '京东': 'jingdong',
  '微信': 'weixin',
  '高德地图': 'gaodeditu',
  '网易新闻': 'wangyixinwen',
  '凤凰新闻': 'fenghuangxinwen',
  '搜狐新闻': 'souhuxinwen'
};

export default {
  name: 'MediaScore',
  data() {
    return {
      tableLoading: false,
      selectedMedia: [],
      searchTag: '',
      mediaList: [],
      originalDetailList: [], // 原始明细数据
      pageSize: 20,
      currentPage: 1,
      weights: {},
      columns: [
        { label: '媒体', prop: 'name', minWidth: 150 },
        { label: '资源丰富度', prop: 'richness', minWidth: 140 },
        { label: '算法用户看重度', prop: 'algorithm', minWidth: 140 },
        { label: '品牌合作成熟度', prop: 'brand', minWidth: 140 },
        { label: '平台流量规模', prop: 'platform', minWidth: 140 },
        { label: '达人资源规模', prop: 'influencer', minWidth: 140 },
        { label: '热榜热度规模', prop: 'hot', minWidth: 140 },
        { label: '历史费效优势', prop: 'history', minWidth: 140 },
        { label: '操作', prop: 'action', width: 100 }
      ],
      detailDialogVisible: false,
      detailDialogTitle: '',
      detailData: []
    }
  },
  computed: {
    filteredMediaList() {
      if (!this.searchTag) return this.mediaList
      
      // 处理多选搜索：将搜索标签按逗号分割
      const searchTerms = this.searchTag.split(',').map(term => term.trim()).filter(term => term)
      
      if (searchTerms.length === 0) return this.mediaList
      
      return this.mediaList.filter(item => 
        searchTerms.some(term => item.name.includes(term))
      )
    },
    tableData() {
      const weightRow = {
        isWeightRow: true,
        name: '权重'
      }
      return [weightRow, ...this.filteredMediaList]
    },
    pagedTableData() {
      const start = (this.currentPage - 1) * this.pageSize;
      return [this.tableData[0], ...this.filteredMediaList.slice(start, start + this.pageSize)];
    }
  },
  mounted() {
    this.getData()
  },
  methods: {
    handlePageChange(page) {
      this.currentPage = page;
    },
    handleSizeChange(size) {
      this.pageSize = size;
      this.currentPage = 1;
    },
    // 获取数据
    getData() {
      this.mediaList = getMediaScoreList() // 获取列表数据（初始为空）
      this.originalDetailList = getOriginalDetailData() // 获取原始明细数据
      this.weights = getWeightConfig()
      // this.page.total = this.filteredMediaList.length // This line is removed as per the new_code
    },
    // 获取行样式类名
    getRowClassName({ row, rowIndex }) {
      if (row.isWeightRow) {
        return 'weight-config-row'
      }
      return ''
    },
    // 搜索媒体
    searchData() {
      this.currentPage = 1
      if (Array.isArray(this.selectedMedia)) {
        // 多选模式：将选中的媒体名称用逗号连接
        this.searchTag = this.selectedMedia.join(', ')
      } else {
        // 单选模式（兼容）
        this.searchTag = this.selectedMedia
      }
      this.getData()
    },
    // 重置搜索
    resetForm() {
      this.selectedMedia = []
      this.searchTag = ''
      this.currentPage = 1
      this.getData()
    },
    // 清除搜索
    clearSearch() {
      this.searchTag = ''
      this.selectedMedia = []
      this.currentPage = 1
      this.getData()
    },
    // 媒体选择变化
    onMediaChange(value) {
      if (Array.isArray(value)) {
        // 多选模式：将选中的媒体名称用逗号连接
        this.searchTag = value.join(', ')
      } else {
        // 单选模式（兼容）
        this.searchTag = value
      }
      this.currentPage = 1
      this.getData()
    },
    // 新增媒体
    onAddMedia() {
      this.$message.info('新增媒体功能待实现')
    },
    // 下载
    onDownload() {
      this.$message.info('下载功能待实现')
    },
    // 权重变更
    onWeightChange(prop) {
      // 实时校验权重总和
      const totalWeight = Object.values(this.weights).reduce((sum, val) => sum + (val || 0), 0)
      if (totalWeight > 100) {
        this.$message.warning('权重总和不能超过100%')
      }
    },
    // 明细 - 显示原始数据
    onViewDetail(row) {
      // 获取原始明细数据
      const originalData = getOriginalMediaDetail(row.name)
      if (!originalData) {
        this.$message.error('未找到该媒体的原始数据')
        return
      }
      this.detailDialogTitle = `${row.name}明细`
      let detailData = []
      if (row.name === '小红书') {
        detailData = [
        {
          name: '资源丰富度',
          score: originalData.richness,
          groups: [
              { name: '曝光类', tags: [ { name: '短视频' }, { name: '热榜' }, { name: '品牌专区' } ] },
              { name: '内容类', tags: [ { name: 'IP' }, { name: '明星' } ] },
              { name: '效果类', tags: [ { name: '推荐' } ] }
              ] 
            },
            { 
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' }, { name: '评论' }, { name: '关注' } ] },
              { name: '用户信息因子', tags: [ { name: 'IP地址信息' }, { name: '兴趣标签' }, { name: '互动频率' }, { name: '历史行为' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '常规硬广/投放' }, { name: '达人内容合作' }, { name: 'IP定制合作' } ] }
              ] 
            },
            { 
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '1亿 < 月活 < 5亿' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '数量 > 100w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 500-1000w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' } ] }
            ]
          }
        ]
      } else if (row.name === '抖音') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '直播' }, { name: '短视频' }, { name: '热榜' }, { name: '品牌专区' }, { name: '达人' } ] },
              { name: '内容类', tags: [ { name: 'IP' }, { name: '明星' }, { name: '信息流广告' }, { name: '优质内容推荐' } ] },
              { name: '效果类', tags: [ { name: '推荐' }, { name: '分享/转发' }, { name: '账号信息' } ] }
          ]
        },
        {
          name: '算法用户看重度',
          score: originalData.algorithm,
          groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' }, { name: '评论' }, { name: '关注' }, { name: '搜索' }, { name: '转发' }, { name: '账号信息' } ] },
              { name: '用户信息因子', tags: [ { name: 'IP地址信息' }, { name: '地理位置信息' }, { name: '兴趣标签' }, { name: '粉丝画像' }, { name: '互动频率' }, { name: '历史行为' } ] }
              ] 
            },
            { 
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '常规硬广/投放' }, { name: '达人内容合作' }, { name: 'IP定制合作' }, { name: '活动商业合作' }, { name: '人群定制合作' } ] }
              ] 
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '1亿 < 月活 < 5亿' }, { name: 'TGI > 100' } ] }
          ]
        },
        {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '数量 > 100w' }, { name: '腰部达人 > 50%' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
          groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 500-1000w' } ] }
            ]
          },
            { 
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '快手') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '创意资源' }, { name: '热榜' }, { name: '品牌专区' }, { name: '直播' }, { name: '达人' }, { name: '话题' }, { name: '广告位' }, { name: '短视频' }, { name: '种草' }, { name: '图文' } ] },
              { name: '内容类', tags: [ { name: 'IP' }, { name: '明星' }, { name: '信息流广告' }, { name: '优质内容推荐' }, { name: '直播推流' } ] },
              { name: '效果类', tags: [ { name: '推荐' }, { name: '分享/转发' }, { name: '发布记录' }, { name: '账号信息' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' }, { name: '收藏' }, { name: '评论' }, { name: '关注' }, { name: '搜索' }, { name: '转发' }, { name: '账号信息' }, { name: '设备信息' } ] },
              { name: '用户信息因子', tags: [ { name: 'IP地址信息' }, { name: '地理位置信息' }, { name: '交易记录' }, { name: '兴趣标签' }, { name: '粉丝画像' }, { name: '消费能力' }, { name: '内容偏好' }, { name: '互动频率' }, { name: '历史行为' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '常规硬广/投放' }, { name: '达人内容合作' }, { name: 'IP定制合作' }, { name: '活动商业合作' }, { name: '人群定制合作' }, { name: '效果转化投放' } ] }
          ]
        },
        {
          name: '平台流量规模',
          score: originalData.platform,
          groups: [
              { name: '', tags: [ { name: '1亿 < 月活 < 5亿' }, { name: 'TGI > 100' } ] }
          ]
        },
        {
          name: '达人资源规模',
          score: originalData.influencer,
          groups: [
              { name: '', tags: [ { name: '数量 > 100w' }, { name: '腰部达人 > 50%' } ] }
              ] 
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 500-1000w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '微博') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '热搜' }, { name: '品牌专区' } ] },
              { name: '内容类', tags: [ { name: '明星' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' } ] },
              { name: '用户信息因子', tags: [ { name: 'IP地址信息' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '常规硬广/投放' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '1亿 < 月活 < 5亿' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '数量 > 100w' } ] }
          ]
        },
        {
          name: '热榜热度规模',
          score: originalData.hot,
          groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 500-1000w' } ] }
              ] 
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' } ] }
            ]
          }
        ]
      } else if (row.name === 'B站') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '番剧' }, { name: 'UP主' }, { name: '专栏' } ] },
              { name: '内容类', tags: [ { name: '鬼畜' }, { name: '动画' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '弹幕' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: 'UP主合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: 'UP主数量' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热门视频' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' } ] }
            ]
          }
        ]
      } else if (row.name === '微信') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '朋友圈广告' }, { name: '公众号' }, { name: '小程序' }, { name: '视频号' }, { name: '搜一搜' }, { name: '看一看' } ] },
              { name: '内容类', tags: [ { name: '图文' }, { name: '视频' }, { name: '直播' }, { name: 'H5' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' }, { name: '评论' }, { name: '分享' }, { name: '收藏' }, { name: '转发' }, { name: '关注' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '朋友圈广告' }, { name: '公众号合作' }, { name: '小程序合作' }, { name: '视频号合作' }, { name: '搜一搜合作' }, { name: '看一看合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活 > 10亿' }, { name: 'TGI > 100' }, { name: '用户粘性高' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '公众号数量 > 1000w' }, { name: '视频号达人 > 500w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热搜TOP3热度值: 1000-2000w' }, { name: '话题热度持续性强' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 20% < 优化幅度 < 60%' }, { name: '观测指标优化幅度: 10% < 优化幅度 < 30%' }, { name: '转化效果稳定' } ] }
            ]
          }
        ];
      } else if (row.name === '知乎') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '热榜' }, { name: '话题' }, { name: '专栏' }, { name: '圆桌' } ] },
              { name: '内容类', tags: [ { name: '问答' }, { name: '文章' }, { name: '视频' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '点击' }, { name: '浏览' }, { name: '点赞' }, { name: '评论' }, { name: '收藏' }, { name: '关注' }, { name: '分享' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '话题合作' }, { name: '专栏合作' }, { name: '圆桌合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '答主数量 > 100w' }, { name: '优质创作者 > 50%' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 500-1000w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '携程') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页轮播' }, { name: '搜索推荐' }, { name: '目的地页' }, { name: '酒店页' } ] },
              { name: '内容类', tags: [ { name: '攻略' }, { name: '游记' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '搜索' }, { name: '浏览' }, { name: '收藏' }, { name: '预订' }, { name: '评价' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '酒店合作' }, { name: '机票合作' }, { name: '景点合作' }, { name: '攻略合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '旅行达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热门目的地热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '去哪儿') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '搜索推荐' }, { name: '目的地页' }, { name: '酒店页' }, { name: '机票页' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '搜索' }, { name: '浏览' }, { name: '收藏' }, { name: '预订' }, { name: '评价' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '酒店合作' }, { name: '机票合作' }, { name: '景点合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '旅行达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热门目的地热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '高德地图') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '地图POI' }, { name: '导航推荐' }, { name: '周边推荐' }, { name: '路线规划' }, { name: '实时路况' }, { name: '公交地铁' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '搜索' }, { name: '导航' }, { name: '收藏' }, { name: '评价' }, { name: '分享' }, { name: '定位' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: 'POI合作' }, { name: '导航合作' }, { name: '周边推荐合作' }, { name: '路线规划合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '地图达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热门地点热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '滴滴出行') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '车型推荐' }, { name: '路线推荐' }, { name: '优惠券' }, { name: '活动页' }, { name: '积分商城' }, { name: '会员中心' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '叫车' }, { name: '浏览' }, { name: '收藏' }, { name: '评价' }, { name: '分享' }, { name: '支付' }, { name: '定位' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '车型合作' }, { name: '路线合作' }, { name: '活动合作' }, { name: '会员合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '司机达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热门路线热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '爱奇艺') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热榜' }, { name: '分类页' }, { name: '搜索推荐' }, { name: 'VIP专区' }, { name: '独播剧' }, { name: '自制剧' }, { name: '综艺' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '观看' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' }, { name: '订阅' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '剧集合作' }, { name: '综艺合作' }, { name: 'VIP合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '明星艺人 > 1000' }, { name: '网红达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 1000-2000w' }, { name: '剧集热度持续性强' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '腾讯视频') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热榜' }, { name: '分类页' }, { name: '搜索推荐' }, { name: 'VIP专区' }, { name: '独播剧' }, { name: '自制剧' }, { name: '综艺' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '观看' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' }, { name: '订阅' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '剧集合作' }, { name: '综艺合作' }, { name: 'VIP合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '明星艺人 > 1000' }, { name: '网红达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 1000-2000w' }, { name: '剧集热度持续性强' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '优酷') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热榜' }, { name: '分类页' }, { name: '搜索推荐' }, { name: 'VIP专区' }, { name: '独播剧' }, { name: '综艺' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '观看' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' }, { name: '订阅' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '剧集合作' }, { name: '综艺合作' }, { name: 'VIP合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '明星艺人 > 1000' }, { name: '网红达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 1000-2000w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '芒果') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热榜' }, { name: '分类页' }, { name: '搜索推荐' }, { name: 'VIP专区' }, { name: '独播剧' }, { name: '综艺' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '观看' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '剧集合作' }, { name: '综艺合作' }, { name: 'VIP合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 5000w-1亿' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '明星艺人 > 1000' }, { name: '网红达人 > 10w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 1000-2000w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '搜狐新闻') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热点' }, { name: '分类页' }, { name: '搜索推荐' }, { name: '专题' }, { name: '评论' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '阅读' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '新闻合作' }, { name: '专题合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 1000w-5000w' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '记者编辑 > 1w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热点TOP3热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '网易新闻') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热点' }, { name: '分类页' }, { name: '搜索推荐' }, { name: '专题' }, { name: '评论' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '阅读' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '新闻合作' }, { name: '专题合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 1000w-5000w' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '记者编辑 > 1w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热点TOP3热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '凤凰新闻') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '热点' }, { name: '分类页' }, { name: '搜索推荐' }, { name: '专题' }, { name: '评论' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '阅读' }, { name: '收藏' }, { name: '点赞' }, { name: '评论' }, { name: '分享' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '新闻合作' }, { name: '专题合作' }, { name: '广告合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活: 1000w-5000w' }, { name: 'TGI > 100' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '记者编辑 > 1w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热点TOP3热度值: 100-500w' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 10% < 优化幅度 < 50%' }, { name: '观测指标优化幅度: 5% < 优化幅度 < 20%' } ] }
            ]
          }
        ]
      } else if (row.name === '淘宝') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '搜索推荐' }, { name: '分类页' }, { name: '直播' }, { name: '短视频' }, { name: '热榜' }, { name: '品牌专区' }, { name: '达人' }, { name: '活动页' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '搜索' }, { name: '浏览' }, { name: '收藏' }, { name: '购买' }, { name: '评价' }, { name: '分享' }, { name: '关注' }, { name: '直播观看' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '店铺合作' }, { name: '商品合作' }, { name: '直播合作' }, { name: '达人合作' }, { name: '活动合作' }, { name: '品牌合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活 > 5亿' }, { name: 'TGI > 100' }, { name: '用户粘性高' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '店铺数量 > 1000w' }, { name: '直播达人 > 100w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 2000-5000w' }, { name: '商品热度持续性强' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 20% < 优化幅度 < 60%' }, { name: '观测指标优化幅度: 10% < 优化幅度 < 30%' }, { name: '转化效果稳定' } ] }
            ]
          }
        ]
      } else if (row.name === '京东') {
        detailData = [
          {
            name: '资源丰富度',
            score: originalData.richness,
            groups: [
              { name: '曝光类', tags: [ { name: '首页推荐' }, { name: '搜索推荐' }, { name: '分类页' }, { name: '直播' }, { name: '短视频' }, { name: '热榜' }, { name: '品牌专区' }, { name: '活动页' } ] }
            ]
          },
          {
            name: '算法用户看重度',
            score: originalData.algorithm,
            groups: [
              { name: '用户行为因子', tags: [ { name: '搜索' }, { name: '浏览' }, { name: '收藏' }, { name: '购买' }, { name: '评价' }, { name: '分享' }, { name: '关注' } ] }
            ]
          },
          {
            name: '品牌合作成熟度',
            score: originalData.brand,
            groups: [
              { name: '', tags: [ { name: '店铺合作' }, { name: '商品合作' }, { name: '直播合作' }, { name: '达人合作' }, { name: '活动合作' } ] }
            ]
          },
          {
            name: '平台流量规模',
            score: originalData.platform,
            groups: [
              { name: '', tags: [ { name: '月活 > 5亿' }, { name: 'TGI > 100' }, { name: '用户粘性高' } ] }
            ]
          },
          {
            name: '达人资源规模',
            score: originalData.influencer,
            groups: [
              { name: '', tags: [ { name: '店铺数量 > 1000w' }, { name: '直播达人 > 100w' } ] }
            ]
          },
          {
            name: '热榜热度规模',
            score: originalData.hot,
            groups: [
              { name: '', tags: [ { name: '热榜TOP3热度值: 2000-5000w' }, { name: '商品热度持续性强' } ] }
            ]
          },
          {
            name: '历史费效优势',
            score: originalData.history,
            groups: [
              { name: '', tags: [ { name: '结算指标优化幅度: 20% < 优化幅度 < 60%' }, { name: '观测指标优化幅度: 10% < 优化幅度 < 30%' }, { name: '转化效果稳定' } ] }
            ]
          }
        ]
      }
      this.detailData = detailData
      this.detailDialogVisible = true
    },
    // 计算
    onCalculate() {
      // 校验权重总和是否为100%
      if (!validateWeights(this.weights)) {
        this.$message.error('权重总和必须为100%，请检查权重配置')
        return
      }
      
      try {
        // 根据权重计算媒体打分
        this.tableLoading = true
        const calculatedData = calculateMediaScores(this.weights)
        this.mediaList = calculatedData
        this.$message.success('计算完成')
      } catch (error) {
        this.$message.error('计算失败：' + error.message)
      } finally {
        this.tableLoading = false
      }
    },
    // 保存
    onSave() {
      try {
        // 更新权重配置
        updateWeightConfig(this.weights)
        
        // 保存媒体打分列表数据
        saveMediaScoreList()
        
        this.$message.success('保存成功')
      } catch (error) {
        this.$message.error('保存失败：' + error.message)
      }
    },
    // 关闭明细弹窗
    handleClose() {
      this.detailDialogVisible = false
      this.detailData = []
    },
    // 获取标签颜色（基于标签名称生成固定颜色）
    getTagColor(tagName) {
      const colors = [
        '#ff6b6b', // 红色
        '#4ecdc4', // 青色
        '#45b7d1', // 蓝色
        '#96ceb4', // 绿色
        '#feca57', // 黄色
        '#ff9ff3', // 粉色
        '#54a0ff', // 天蓝色
        '#5f27cd', // 紫色
        '#00d2d3', // 青绿色
        '#ff9f43', // 橙色
        '#10ac84', // 深绿色
        '#ee5253', // 深红色
        '#0abde3', // 亮蓝色
        '#ff3838', // 亮红色
        '#2ed573', // 亮绿色
        '#ff4757', // 亮红色
        '#3742fa', // 深蓝色
        '#2f3542', // 深灰色
        '#ffa502', // 橙色
        '#2ed573', // 绿色
        '#1e90ff', // 蓝色
        '#ff6348', // 红色
        '#ffa502', // 橙色
        '#2ed573', // 绿色
        '#1e90ff', // 蓝色
        '#ff4757', // 亮红色
        '#3742fa', // 深蓝色
        '#2f3542', // 深灰色
        '#ffa502', // 橙色
        '#2ed573', // 绿色
        '#1e90ff', // 蓝色
        '#ff6348', // 红色
        '#ffa502', // 橙色
        '#2ed573', // 绿色
        '#1e90ff'  // 蓝色
      ];
      
      // 基于标签名称生成固定的颜色索引
      let hash = 0;
      for (let i = 0; i < tagName.length; i++) {
        hash = ((hash << 5) - hash) + tagName.charCodeAt(i);
        hash = hash & hash; // 转换为32位整数
      }
      const color = colors[Math.abs(hash) % colors.length];
      return color;
    },
    getTagColorClass(tagName) {
      const colorList = [
        'red', 'green', 'blue', 'purple', 'yellow', 'orange', 'teal', 'pink', 'cyan', 'indigo'
      ];
      let hash = 0;
      for (let i = 0; i < tagName.length; i++) {
        hash = ((hash << 5) - hash) + tagName.charCodeAt(i);
        hash = hash & hash;
      }
      return colorList[Math.abs(hash) % colorList.length];
    },
    getTagColorStyle(tagName) {
      const tagColorMap = {
        red:    { text: '#e74c3c', bg: '#fdecec', border: '#fad2d2' },
        green:  { text: '#27ae60', bg: '#eafaf1', border: '#c6f0d6' },
        blue:   { text: '#2563eb', bg: '#eaf1fe', border: '#bcd3fa' },
        purple: { text: '#8e44ad', bg: '#f5eafd', border: '#e0c6fa' },
        yellow: { text: '#f39c12', bg: '#fff9e6', border: '#ffe6a1' },
        orange: { text: '#ff9800', bg: '#fff3e0', border: '#ffd8a6' },
        teal:   { text: '#1abc9c', bg: '#e6fcfa', border: '#b6f0e7' },
        pink:   { text: '#e84393', bg: '#fdeaf4', border: '#f7c6e0' },
        cyan:   { text: '#00bcd4', bg: '#e0f7fa', border: '#b2ebf2' },
        indigo: { text: '#3f51b5', bg: '#e8eaf6', border: '#c5cae9' }
      };
      const colorKey = this.getTagColorClass(tagName);
      const color = tagColorMap[colorKey];
      return {
        color: color.text,
        background: color.bg,
        border: `1px solid ${color.border}`
      };
    },
    getMediaLogo(name) {
      const pinyin = mediaPinyinMap[name] || name;
      return logoMap[pinyin] || '';
    },
    getTagColorClassByGroup(groupName) {
      const colorList = [
        'green', 'blue', 'yellow', 'orange', 'teal', 'cyan', 'indigo'
      ];
      let hash = 0;
      for (let i = 0; i < groupName.length; i++) {
        hash = ((hash << 5) - hash) + groupName.charCodeAt(i);
        hash = hash & hash;
      }
      return colorList[Math.abs(hash) % colorList.length];
    },
    getTagColorStyleByGroup(groupName) {
      // 手动指定常用分组主色
      const manualMap = {
        '曝光类': 'blue',
        '内容类': 'teal',
        '效果类': 'orange'
      };
      const colorList = ['green', 'blue', 'yellow', 'orange', 'teal', 'cyan', 'indigo'];
      const tagColorMap = {
        green:  { text: '#27ae60', bg: '#eafaf1', border: '#c6f0d6' },
        blue:   { text: '#2563eb', bg: '#eaf1fe', border: '#bcd3fa' },
        yellow: { text: '#f39c12', bg: '#fff9e6', border: '#ffe6a1' },
        orange: { text: '#ff9800', bg: '#fff3e0', border: '#ffd8a6' },
        teal:   { text: '#1abc9c', bg: '#e6fcfa', border: '#b6f0e7' },
        cyan:   { text: '#00bcd4', bg: '#e0f7fa', border: '#b2ebf2' },
        indigo: { text: '#3f51b5', bg: '#e8eaf6', border: '#c5cae9' }
      };
      let colorKey = manualMap[groupName];
      if (!colorKey) {
        let hash = 0;
        for (let i = 0; i < groupName.length; i++) {
          hash = ((hash << 5) - hash) + groupName.charCodeAt(i);
          hash = hash & hash;
        }
        colorKey = colorList[Math.abs(hash) % colorList.length];
      }
      const color = tagColorMap[colorKey];
      return {
        color: color.text,
        background: color.bg,
        border: `1px solid ${color.border}`
      };
    },
    getRankClass(index) {
      if (index === 0) return 'rank-1';
      if (index === 1) return 'rank-2';
      if (index === 2) return 'rank-3';
      return 'rank-normal';
    }
  }
}
</script>

<style lang="scss" scoped>
$border: 1px solid #eaebf0;
$radius: 8px;

.media-score-page {
  padding: 0 32px 32px 32px;
  background: #f5f8fd;
  min-height: 100vh;
}
.card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.06);
  padding: 24px 32px;
}
.filter-card {
  margin-bottom: 24px;
}
.content-card {
  margin-bottom: 0;
}
.date-filter {
  .search {
    display: flex;
    align-items: center;
    .label {
      font-size: 15px;
      color: #222;
      margin-right: 12px;
      height: 32px;
      line-height: 32px;
      display: flex;
      align-items: center;
    }
    .search-input {
      // 不再强制height: 36px
      .el-input__inner {
        // 不再强制height/line-height
        font-size: 14px;
      }
    }
    .el-select--small .el-input__inner {
      font-size: 14px;
      padding: 0 12px;
    }
  }
  .search-tag {
    display: inline-flex;
    align-items: center;
    margin-left: 16px;
    .text {
      color: #648bd8;
      font-size: 14px;
    }
    .close {
      margin-left: 4px;
      cursor: pointer;
      color: #b0b8c9;
      font-size: 16px;
    }
  }
  .el-button {
    border-radius: 8px;
    margin-left: 8px;
  }
}
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  .el-button {
    border-radius: 8px;
    margin-left: 8px;
  }
}
.content {
  margin-top: 0;
}
.el-table {
  background: #fff;
  border-radius: 12px;
  box-shadow: none;
  th, td {
    background: #fff;
    border-bottom: 1px solid #f0f1f5;
  }
  th {
    color: #7b8190;
    font-weight: 600;
    /* font-size: 15px; */
    background: #f5f7fa;
  }
  td {
    color: #222;
    /* font-size: 15px; */
  }
  .el-button[type="text"] {
    color: #648bd8;
    font-weight: 500;
    border-radius: 8px;
    padding: 0 !important;
    margin: 0 !important;
    min-width: 0;
    text-align: left;
    &:hover {
      background: #f5f7fa;
    }
  }
}
::v-deep .el-table .el-button[type="text"] {
  padding: 0 !important;
  margin: 0 !important;
  min-width: 0 !important;
  text-align: left !important;
  background: none !important;
  box-shadow: none !important;
  border: none !important;
}

// 权重配置行样式
::v-deep .weight-config-row {
  background-color: #f8f9fa;
  border-bottom: 2px solid #e9ecef;
  
  td {
    padding: 0 0 15px 0 !important;
    border-bottom: 2px solid #e9ecef !important;
  }
}

// 权重配置单元格样式
.weight-cell {
  display: flex;
  align-items: center;
  
  .weight-label {
    font-weight: 600;
    color: #409eff;
    /* font-size: 14px; */
    
    i {
      margin-right: 5px;
    }
  }
  
  .weight-input-container {
    display: flex;
    align-items: center;
    .weight-input {
      width: 100px;
      min-width: 0;
      display: flex;
      align-items: center;
      ::v-deep .el-input__inner {
        width: 100% !important;
        min-width: 0;
        background: #fff !important;
        border: 1px solid #e5e9f2 !important;
        border-radius: 8px 0 0 8px !important;
        box-shadow: none !important;
        height: 32px;
        font-size: 15px;
        text-align: center;
        padding: 0 8px;
      }
      ::v-deep .el-input-group__append {
        background: #fff !important;
        border: 1px solid #e5e9f2 !important;
        border-left: none !important;
        border-radius: 0 8px 8px 0 !important;
        color: #7b8190;
        font-size: 15px;
        height: 32px;
        display: flex;
        align-items: center;
        box-shadow: none !important;
        min-width: 30px;
        padding: 0 8px;
        white-space: nowrap;
      }
      ::v-deep .el-input__wrapper {
        box-shadow: none !important;
      }
    }
  }
}

// 媒体logo样式
.media-name-cell {
  display: flex;
  align-items: center;
  .media-logo {
    width: 38px;
    height: 38px;
    border-radius: 8px;
    object-fit: cover;
    margin-right: 12px;
    background: #f5f7fa;
    border: 1px solid #e5e9f2;
  }
}

// 明细弹窗样式
.detail-dialog {
  .el-dialog {
    background: #fff;
    border-radius: 16px !important;
    box-shadow: 0 4px 24px rgba(0,0,0,0.08);
    padding: 0;
  }
  .el-dialog__header {
    background: #f5f7fa;
    border-radius: 16px 16px 0 0;
    font-size: 18px;
    font-weight: 700;
    color: #222;
    border-bottom: 1px solid #f0f1f5;
  }
  .el-dialog__body {
    padding: 24px 32px 16px 32px;
    background: #fff;
  }
  .detail-content {
    background: #fff;
    border-radius: 12px;
    padding: 0;
    
    .el-table {
      border-radius: 12px;
      overflow: hidden;
      
      th {
        background: #eaf1fe !important;
        color: #2563eb !important;
        font-weight: 700;
        font-size: 16px;
        border-bottom: 2px solid #bcd3fa;
        text-shadow: none;
        letter-spacing: 1px;
      }
      
      td {
        padding: 16px 12px;
        border-bottom: 1px solid #f0f1f5;
      }
    }
    
    .dimension-title {
      color: #2c3e50;
      font-weight: 600;
      font-size: 14px;
    }
    
    .dimension-groups {
      padding: 8px 0;
      .tag-group {
        margin-bottom: 16px;
        
        &:last-child {
          margin-bottom: 0;
        }
        
        .group-title {
          color: #44444f;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 12px;
          display: block;
        }
        
        .tag-list {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          
          .custom-tag {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            border-radius: 999px;
            font-size: 12px;
            font-weight: 500;
            padding: 0 12px;
            height: 28px;
            transition: all 0.2s;
            user-select: none;
            min-width: 42px;
            border: 1px solid transparent;
          }
        }
      }
    }
    
    .dimension-score {
      color: #2563eb;
      font-weight: 600;
      font-size: 15px;
      text-align: center;
    }
  }
  
  .dialog-footer {
    padding: 16px 32px 24px 32px;
    display: flex;
    justify-content: flex-end;
    .el-button {
      border-radius: 8px;
      font-size: 14px;
      padding: 0 24px;
      height: 36px;
      background: #2563eb;
      color: #fff;
      border: none;
      font-weight: 500;
      &:hover {
        background: #1e40af;
      }
    }
  }
}
.pagination-bar {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}
.tag-red    { color: #e74c3c; background: #fdecec; border-color: #fad2d2; }
.tag-green  { color: #27ae60; background: #eafaf1; border-color: #c6f0d6; }
.tag-blue   { color: #2563eb; background: #eaf1fe; border-color: #bcd3fa; }
.tag-purple { color: #8e44ad; background: #f5eafd; border-color: #e0c6fa; }
.tag-yellow { color: #f39c12; background: #fff9e6; border-color: #ffe6a1; }
.tag-orange { color: #ff9800; background: #fff3e0; border-color: #ffd8a6; }
.tag-teal   { color: #1abc9c; background: #e6fcfa; border-color: #b6f0e7; }
.tag-pink   { color: #e84393; background: #fdeaf4; border-color: #f7c6e0; }
.tag-cyan   { color: #00bcd4; background: #e0f7fa; border-color: #b2ebf2; }
.tag-indigo { color: #3f51b5; background: #e8eaf6; border-color: #c5cae9; }
.plain-detail-btn {
  display: inline-block;
  background: none;
  border: none;
  outline: none;
  color: #2563eb;
  font-size: 14px;
  font-weight: 500;
  padding: 0 !important;
  margin: 0;
  min-width: 0;
  text-align: left;
  cursor: pointer;
  border-radius: 4px;
  transition: background 0.2s;
}
.plain-detail-btn:hover {
  color: #1e40af;
}
.media-rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  margin-right: 8px;
}
.rank-1 {
  background: linear-gradient(135deg, #f59e0b, #fbbf24);
}
.rank-2 {
  background: linear-gradient(135deg, #2563eb, #60a5fa);
}
.rank-3 {
  background: linear-gradient(135deg, #22c55e, #4ade80);
}
.rank-normal {
  background: #b0b8c9;
}
</style> 